/**
 * Component Registry for The Majestic
 * 
 * Inherits from @vibesites-weidner-apartment-homes/theme-via6 and allows property-specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as parentRegistry } from '@vibesites-weidner-apartment-homes/theme-via6/registry';
import { Header } from './components/Header';
import { InteractiveMapSection } from './components/InteractiveMapSection';
import { AmenitiesGrid } from './components/AmenitiesGrid';
import { AmenitiesListCommunity } from './components/AmenitiesListCommunity';
import { AmenitiesListInterior } from './components/AmenitiesListInterior';
import { FloorplansList } from './components/FloorplansList';
import { GalleryTabs } from './components/GalleryTabs';

/**
 * Property-specific component overrides.
 * Uncomment and customize components as needed.
 */
const propertyOverrides: Partial<ComponentRegistry> = {
  // Forked from the Weidner Header so we can pin the header logo to
  // `property_mono_white_logo` instead of the parent's `property_primary_logo`.
  // The parent uses a MutationObserver to force its choice of src/srcset onto
  // the rendered <img>, so a pure style wrapper or slot-default override
  // couldn't beat it — see src/components/Header.tsx for details.
  Header,
  // Forked from theme-via6 to add a scroll-gate overlay over the embedded
  // ApartmentSync/Repli360 leasing widget. The widget captures wheel events
  // (zooming the map / scrolling the rooms list), which trapped page scrolling
  // and made it hard to scroll back to the top of the page — see
  // src/components/InteractiveMapSection.tsx.
  InteractiveMapSection,
  // Style-wrapper override for the home-page amenities icon grid: enlarge
  // the icon glyphs and label text so the aspect-square tiles read fuller
  // instead of leaving lots of empty space — see src/components/AmenitiesGrid.tsx.
  AmenitiesGrid,
  // Style-wrapper overrides for the two amenities-page rows: capitalize the
  // amenity list items and hide the "Amenities will be displayed here."
  // empty-state placeholder. Both tweaks live inside the parent's shared
  // `_AmenitiesListRow` JSX, but are reachable via CSS scoped to
  // `[data-weidner-amenities-row]` — see the component files for details.
  AmenitiesListCommunity,
  AmenitiesListInterior,
  // Style-wrapper override for the home-page floor-plan cards: the parent's
  // hover iris-expands a gray-reading primary overlay over 0.55s and delays
  // the CTA pill a further 0.35s, which felt sluggish. We retune only the
  // hover timings (faster clip-path, no CTA delay) — see
  // src/components/FloorplansList.tsx.
  FloorplansList,
  // Forked from theme-via6 so the gallery page presents photos as a
  // click-to-enlarge grid on mobile instead of the parent's 40-slide swipe
  // carousel (customer request). The mobile carousel lives inside the shared
  // `_galleryShared` GalleryRow, which we can't edit — so we replace GalleryRow
  // with a local grid rendered at every breakpoint and reuse the shared
  // lightbox for the enlarge view — see src/components/GalleryTabs.tsx.
  GalleryTabs,
};

/**
 * Final merged registry for this property.
 * Inherits all components from the parent theme, with property overrides.
 */
export const registry: ComponentRegistry = {
  ...parentRegistry,
  ...propertyOverrides,
};

export default registry;
